import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { isBloggerBusiness } from '../../utils/accessService';
import { cropPublicId } from '../../utils/cropPublicId';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { Button } from '../input/Button';
import { Box } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { Flex, FlexProps } from '../layout/Flex';
import { Avatar } from '../media/Avatar';
import { Typography } from '../typography/Typography';
import type { BusinessUI } from './types';

export const calculateHeight = () => 60;

const Root = styled(Flex).withConfig({ displayName: 'Root' }).attrs({
    py: 1.5,
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
})`
    border-bottom: 1px solid var(--divider);

    &:last-child {
        border-bottom-color: transparent;
    }
`;

interface BusinessListItemProps extends FlexProps {
    business: BusinessUI;
    onSubscribe: (id: BusinessUI) => void;
    onClick?: VoidFunction;
}

export const BusinessListItem = (props: BusinessListItemProps) => {
    const { business, onClick, ...other } = props;
    const t = useTranslation();

    const onSubscribe = useCallback(() => {
        props.onSubscribe(business);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [business, props.onSubscribe]);

    const title = isBloggerBusiness(business.type)
        ? business.displayName
        : cropPublicId(business.publicId);
    const subtitle = isBloggerBusiness(business.type) ? undefined : business.displayName;

    return (
        <Root {...other} dataTestId={DataTestId.BUSINESS_SEARCH_ROW} spacing={1}>
            <Flex
                as={Link}
                to={business.url}
                direction="row"
                alignItems="center"
                textDecoration="none"
                spacing={1}
                onClick={onClick}
                overflow="hidden"
            >
                <Avatar
                    title={business.displayName}
                    src={business.avatarUrl}
                    style={{ flexShrink: 0 }}
                />
                <Box overflow="hidden">
                    <Typography
                        as="h3"
                        variant="caption"
                        weight="bold"
                        overflow="hidden"
                        textOverflow="ellipsis"
                    >
                        {title}
                    </Typography>
                    <Typography as="span" variant="caption" weight="medium" color="secondary">
                        {subtitle}
                    </Typography>
                </Box>
            </Flex>
            <Button
                size="s"
                color="secondary"
                onClick={onSubscribe}
                dataTestId={business.subscribed ? DataTestId.SUBSCRIBED : DataTestId.UNSUBSCRIBED}
            >
                {business.subscribed ? t('Вы подписаны') : t('Подписаться')}
            </Button>
        </Root>
    );
};

export const BusinessListItemSkeleton = (props: FlexProps) => (
    <Root {...props}>
        <Flex direction="row" alignItems="center" textDecoration="none" spacing={1}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box>
                <Skeleton variant="text" width={150} height={14} />
                <Skeleton variant="text" width={120} height={14} />
            </Box>
        </Flex>
        <Skeleton variant="rect" width={120} height={34} borderRadius="999px" />
    </Root>
);
